import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Content Approval Queue
 * Review, edit, and approve AI-generated content
 * Blog posts auto-publish to website when approved!
 */
const ContentApprovalQueue = () => {
  const [draftContent, setDraftContent] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [publishing, setPublishing] = useState(false);

  const loadDrafts = () => {
    const savedDrafts = localStorage.getItem('ai_draft_content');
    if (savedDrafts) {
      setDraftContent(JSON.parse(savedDrafts));
    }
  };

  useEffect(() => {
    // Load draft content from localStorage (temporary storage)
    loadDrafts();
    
    // Listen for storage changes (when AI generates new content)
    const handleStorageChange = (e) => {
      if (e.key === 'ai_draft_content') {
        loadDrafts();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also poll every 2 seconds for same-window updates
    const interval = setInterval(loadDrafts, 2000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Auto-publish blog post to website
  const publishBlogToWebsite = async (content) => {
    try {
      const formData = new FormData();
      formData.append('title', content.title || 'Untitled Post');
      formData.append('excerpt', content.excerpt || content.content?.substring(0, 200) + '...');
      formData.append('content', content.content || '');
      formData.append('author', content.author || 'PricePAL Compare AI');
      formData.append('category', content.category || 'Money Saving Tips');
      formData.append('image', content.image_url || content.image || '');
      formData.append('status', 'published');

      const response = await fetch(`${BACKEND_URL}/api/admin/blog/posts`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Failed to publish' };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleApprove = async (contentId) => {
    const content = draftContent.find(c => c.id === contentId);
    if (!content) return;

    // If it's a blog post, auto-publish to website
    if (content.type === 'blog') {
      const confirmPublish = window.confirm(
        `📝 Approve & Publish Blog Post?\n\n"${content.title}"\n\nThis will automatically publish to the website's blog.`
      );
      
      if (!confirmPublish) return;

      setPublishing(true);
      const result = await publishBlogToWebsite(content);
      setPublishing(false);

      if (result.success) {
        // Remove from drafts and mark as published
        const updatedContent = draftContent.filter(c => c.id !== contentId);
        setDraftContent(updatedContent);
        localStorage.setItem('ai_draft_content', JSON.stringify(updatedContent));
        setSelectedContent(null);

        alert(`✅ Blog Published to Website!\n\n"${content.title}"\n\nView it at: /blog`);
        return;
      } else {
        alert(`❌ Failed to publish: ${result.error}\n\nContent saved as approved draft.`);
      }
    }

    // For non-blog content, just mark as approved
    const updatedContent = draftContent.map(c => 
      c.id === contentId 
        ? { ...c, status: 'approved', approved_at: new Date().toISOString() }
        : c
    );

    setDraftContent(updatedContent);
    localStorage.setItem('ai_draft_content', JSON.stringify(updatedContent));
    setSelectedContent(null);

    alert(`✅ Content approved!\n\n"${content.title}"\n\nYou can now schedule or publish this content.`);
  };

  const handleReject = (contentId) => {
    if (!window.confirm('Are you sure you want to reject this content? It will be permanently deleted.')) {
      return;
    }

    const updatedContent = draftContent.filter(c => c.id !== contentId);
    setDraftContent(updatedContent);
    localStorage.setItem('ai_draft_content', JSON.stringify(updatedContent));
    setSelectedContent(null);

    alert('❌ Content rejected and deleted.');
  };

  const handleEdit = (content) => {
    setSelectedContent(content);
    setEditedContent(content.content);
    setEditMode(true);
  };

  const handleSaveEdit = () => {
    const updatedContent = draftContent.map(c => 
      c.id === selectedContent.id 
        ? { ...c, content: editedContent, edited: true }
        : c
    );

    setDraftContent(updatedContent);
    localStorage.setItem('ai_draft_content', JSON.stringify(updatedContent));
    setEditMode(false);
    setSelectedContent(null);

    alert('✅ Changes saved!');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold">📝 DRAFT</span>;
      case 'approved':
        return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">✅ APPROVED</span>;
      case 'scheduled':
        return <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">📅 SCHEDULED</span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">{status}</span>;
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      blog: '📝',
      social: '📱',
      image: '🎨',
      audio: '🎙️',
      video: '🎬',
      email: '✉️'
    };
    return icons[type] || '📄';
  };

  const pendingCount = draftContent.filter(c => c.status === 'draft').length;
  const approvedCount = draftContent.filter(c => c.status === 'approved').length;

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          📋 Content Approval Queue
        </h2>
        <p className="text-gray-600">
          Review and approve AI-generated content. <strong>Blog posts auto-publish to website when approved!</strong>
        </p>
        {publishing && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-blue-700">Publishing to website...</span>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
          <div className="text-sm text-gray-600">Pending Review</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">{approvedCount}</div>
          <div className="text-sm text-gray-600">Approved</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">0</div>
          <div className="text-sm text-gray-600">Published</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-600">{draftContent.length}</div>
          <div className="text-sm text-gray-600">Total</div>
        </div>
      </div>

      {/* Content List */}
      {draftContent.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-6xl mb-4">📭</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No content yet</h3>
          <p className="text-gray-500">Generate content using the AI Content Generator above</p>
        </div>
      ) : (
        <div className="space-y-4">
          {draftContent.map((content) => (
            <div
              key={content.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start space-x-3">
                  <span className="text-3xl">{getTypeIcon(content.type)}</span>
                  <div>
                    <h3 className="font-bold text-gray-900">{content.title}</h3>
                    <div className="flex items-center flex-wrap gap-2 mt-1">
                      {getStatusBadge(content.status)}
                      <span className="text-xs text-gray-500">{content.service}</span>
                      {content.type === 'blog' && content.status === 'draft' && (
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-medium">
                          🚀 Auto-publishes to website
                        </span>
                      )}
                      {content.is_placeholder && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                          ⚠️ Placeholder content
                        </span>
                      )}
                      {content.edited && (
                        <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">Edited</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {new Date(content.created_at).toLocaleDateString()}
                </div>
              </div>

              {/* Content Preview */}
              <div className="bg-gray-50 rounded p-3 mb-3">
                <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">
                  {content.content}
                </p>
              </div>

              {/* Action Buttons */}
              {content.status === 'draft' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(content)}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-semibold"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleApprove(content.id)}
                    className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-semibold"
                  >
                    ✅ Approve for Use
                  </button>
                  <button
                    onClick={() => handleReject(content.id)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-semibold"
                  >
                    ❌
                  </button>
                </div>
              )}

              {content.status === 'approved' && (
                <div className="flex space-x-2">
                  <button
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-semibold"
                    onClick={() => alert('🚀 Publishing feature coming soon!\n\nThis will allow one-click publishing to your platforms.')}
                  >
                    📅 Schedule/Publish
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-semibold"
                    onClick={() => alert('💾 Export feature coming soon!')}
                  >
                    💾 Export
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editMode && selectedContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-gray-900">
                ✏️ Edit Content
              </h3>
              <button
                onClick={() => {
                  setEditMode(false);
                  setSelectedContent(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={selectedContent.title}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                readOnly
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Content
              </label>
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="15"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setEditMode(false);
                  setSelectedContent(null);
                }}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition"
              >
                💾 Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentApprovalQueue;
